/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createLocalZustand } from "@/shared/state";
import { produce } from "immer";
import { formatFormValues } from "@services";
import { v1 as uuid } from "uuid";
import type { AgencyContextType } from "./AgenciesContext.types";

export type {
  AgencyValuesType,
  MemberType,
  MembershipStatus,
  PropertyValues,
  AgencyContextType,
  HandleChangeStore,
  HandleChangeValues,
  HandleChangePropertiesFilters,
} from "./AgenciesContext.types";

export const agencyContext = createLocalZustand<AgencyContextType>(
  (initializer) => (set, get) => ({
    agency: { ...initializer },
    membershipStatus: 'loading',
    fetchId: uuid(),
    propertiesFetchId: uuid(),
    properties: [],
    propertiesFilters: {},
    set: set,
    get: get,
/**
 * setMembershipStatus function.
 *
 * @param {MembershipStatus} status - The resolved membership status.
 *
 * @returns {void}
 */

    setMembershipStatus: (status) =>
      set((state) => {
        const newState = produce(state, (draft) => {
          draft.membershipStatus = status;
        });
        return newState;
      }),
/**
 * setAgency function.
 *
 * @param {*} agency - Parameter description
 *
 * @returns {*} Return value description
 */

    setAgency: (agency) =>
      set((state) => {
        const newState = produce(state, (draft) => {
          draft.agency = agency;
        });
        return newState;
      }),
/**
 * updateAgency function.
 *
 * @param {*} partialAgency - Parameter description
 *
 * @returns {*} Return value description
 */

    updateAgency: (partialAgency) =>
      set((state) => {
        const newState = produce(state, (draft) => {
          draft.agency = { ...draft.agency, ...partialAgency };
        });
        return newState;
      }),
/**
 * setProperties function.
 *
 * @param {*} properties - Parameter description
 *
 * @returns {*} Return value description
 */

    setProperties: (properties) =>
      set((state) => {
        const newState = produce(state, (draft) => {
          draft.properties = properties;
        });
        return newState;
      }),
/**
 * refreshFetch function.
 *
 * @returns {*} Return value description
 */

    refreshFetch: () =>
      set((state) => {
        const newState = produce(state, (draft) => {
          draft.fetchId = uuid();
        });
        return newState;
      }),
/**
 * refreshPropertiesFetch function.
 *
 * @returns {*} Return value description
 */

    refreshPropertiesFetch: () =>
      set((state) => {
        const newState = produce(state, (draft) => {
          draft.propertiesFetchId = uuid();
        });
        return newState;
      }),
/**
 * setPropertiesFilters function.
 *
 * @param {*} filters - Parameter description
 *
 * @returns {*} Return value description
 */

    setPropertiesFilters: (filters) =>
      set((state) => {
        const newState = produce(state, (draft) => {
          const formattedFilters = formatFormValues(filters);
          console.log("filters", JSON.stringify(formatFormValues(formattedFilters)));
          console.log("draft.propertiesFilters", JSON.stringify(draft.propertiesFilters));
          if (JSON.stringify(draft.propertiesFilters) !== JSON.stringify(formattedFilters)) {
            draft.propertiesFilters = formattedFilters;
          }
        });
        return newState;
      }),
/**
 * updatePropertiesFilters function.
 *
 * @param {*} filters - Parameter description
 *
 * @returns {*} Return value description
 */

    updatePropertiesFilters: (filters) =>
      set((state) => {
        const newState = produce(state, (draft) => {
          const formattedFilters = formatFormValues({ ...draft.propertiesFilters, ...filters });
          console.log("filters", JSON.stringify(formatFormValues(formattedFilters)));
          console.log("draft.propertiesFilters", JSON.stringify(draft.propertiesFilters));
          if (JSON.stringify(draft.propertiesFilters) !== JSON.stringify(formattedFilters)) {
            draft.propertiesFilters = formattedFilters;
          }
        });
        return newState;
      }),
  })
);

export const { useStore: useAgency } = agencyContext;
